import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import * as Slider from '@radix-ui/react-slider';
import { csvParse, max } from 'd3';
import debounce from 'lodash/debounce';

import { bgRadialGradient } from '../../components/Overview/const';
import logoPath from '../../components/Overview/logo.svg';
import { ChartContainer } from './ChartContainer';
import { Map, MapLocation } from './Map';
import { cadData, ukData } from './const';
import { CadLiteLocation } from './types';

/**
 * Determine the maximum power value from the locations array and round it up to the nearest 0.1
 */
function determineMaxPower(locations: CadLiteLocation[]) {
  const allPowerValues = locations.flatMap((location) =>
    Object.keys(location)
      .filter((key) => key.includes('power_kw_'))
      .map((powerKey) => {
        const value = Number(location[powerKey as keyof CadLiteLocation] ?? 0);

        return Math.abs(value);
      }),
  );
  const rawMax = max(allPowerValues);
  const ceiledMaxPower = Math.ceil((rawMax ?? 0) * 10) / 10;
  return Number(ceiledMaxPower.toFixed(1));
}

function getTimeString(intervalIndex: number) {
  // Offset by 15 minutes as data starts at 00:15
  const minutes = intervalIndex * 15 + 15;
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;

  return `${hour}:${minute === 0 ? '00' : minute}`;
}

function CADLite() {
  const [locations, setLocations] = useState<CadLiteLocation[] | undefined>(
    undefined,
  );
  const [sliderValue, setSliderValue] = useState<[number, number]>([0, 0]);

  // To improve the rendering performance, we want the slider time to update immediately, but the map updates to be throttled.
  // A better rendering improvement may be to be more sensible with D3 (not removing and re-adding the pins every time), but this
  // suffices for now.
  const [fifteenMinuteIndexValue, setFifteenMinuteIndexValue] = useState(0);
  const [powerKeyIndex, setPowerKeyIndex] = useState<number>(0);

  useEffect(() => {
    async function fetchCSV() {
      const res = await fetch(
        `${process.env.PUBLIC_URL}/cadlite-locations.csv`,
      );
      const text = await res.text();

      setLocations(csvParse(text) as unknown as CadLiteLocation[]);
    }

    fetchCSV();
  }, []);

  const maxPower = useMemo(
    () => determineMaxPower(locations ?? []),
    [locations],
  );

  useLayoutEffect(() => {
    if (sliderValue[1] === 0 && !!locations) {
      setSliderValue([0, maxPower]);
    }
  }, [locations, maxPower, sliderValue]);

  const filteredLocations: MapLocation[] = useMemo(
    () =>
      (locations
        ?.map((location) => {
          const powerKey = `power_kw_${powerKeyIndex}`;
          const power_kw = location[powerKey as keyof CadLiteLocation];

          return {
            latitude: Number(location.latitude_peturbed),
            longitude: Number(location.longitude_peturbed),
            power_kw: power_kw
              ? Number(location[powerKey as keyof CadLiteLocation])
              : 0,
          };
        })
        .filter(
          ({ power_kw }) =>
            Math.abs(Number(power_kw)) >= sliderValue[0] &&
            Math.abs(Number(power_kw)) <= sliderValue[1],
        ) as MapLocation[]) ?? [],
    [locations, powerKeyIndex, sliderValue],
  );

  const throttledSetPowerKeyIndex = useMemo(
    () =>
      debounce(
        (value: number) => {
          setPowerKeyIndex(value);
        },
        50,
        {
          trailing: true,
        },
      ),
    [],
  );

  return (
    <div
      className="grid min-h-screen auto-rows-min grid-rows-[auto_1fr] gap-4 text-neutral-5 md:grid-cols-[30%_1fr] lg:grid-rows-1"
      style={{
        backgroundColor: 'hsla(266,69%,15%,1)',
        backgroundImage: bgRadialGradient,
      }}
    >
      <div className="grid min-w-0 auto-rows-min gap-8 p-4">
        <img className="self-center" alt="KrakenFlex logo" src={logoPath} />
        <div className="grid gap-2">
          <p>
            <span className="font-medium">Power usage:</span> {sliderValue[0]}{' '}
            kW to {sliderValue[1]} kW
          </p>
          {!!locations && (
            <Slider.Root
              defaultValue={[0, maxPower]}
              disabled={!locations}
              min={0}
              max={maxPower}
              step={0.1}
              minStepsBetweenThumbs={0.1}
              className="relative flex h-2 w-80 items-center rounded-full bg-neutral-400 p-4"
              onValueChange={(value) => {
                setSliderValue(value as [number, number]);
              }}
            >
              <div className="relative flex h-6 w-full items-center">
                <Slider.Track className="relative flex flex-auto items-center">
                  <Slider.Range className="absolute h-1 bg-neutral-300" />
                </Slider.Track>
                <Slider.Thumb className="block h-4 w-4 cursor-pointer rounded-full bg-neutral-200 shadow-md" />
                <Slider.Thumb className="block h-4 w-4 cursor-pointer rounded-full bg-neutral-200 shadow-md" />
              </div>
            </Slider.Root>
          )}
        </div>
        <div className="grid gap-2">
          <p>
            <span className="font-medium">Time of day:</span>{' '}
            {getTimeString(fifteenMinuteIndexValue)}
          </p>
          {!!locations && (
            <Slider.Root
              value={[fifteenMinuteIndexValue]}
              defaultValue={[0]}
              min={0}
              max={95}
              step={1}
              className="relative flex h-2 w-80 items-center rounded-full bg-neutral-400  p-4 "
              onValueChange={(value) => {
                setFifteenMinuteIndexValue(value[0]);
                throttledSetPowerKeyIndex(value[0]);
              }}
            >
              <div className="relative flex h-6 w-full items-center">
                <Slider.Track className="h-1 w-full rounded-full bg-neutral-300"></Slider.Track>
                <Slider.Thumb className="block h-4 w-4 cursor-pointer rounded-full bg-neutral-200 shadow-md" />
              </div>
            </Slider.Root>
          )}
        </div>
        <ChartContainer
          currentSelectedIndex={fifteenMinuteIndexValue}
          data={cadData}
          tickFormat={(value) => `${value.valueOf() / 1000} MW`}
          title="Max CAD Load"
        />
        <ChartContainer
          // Half hourly data for UK load, so need to divide by 2
          currentSelectedIndex={Math.floor(fifteenMinuteIndexValue / 2)}
          data={ukData}
          tickFormat={(value) => `${value.valueOf() / 1000} GW`}
          title="UK Load"
        />
      </div>
      <Map locations={filteredLocations} loading={!locations} />
    </div>
  );
}

export default CADLite;
